import {
  useCallbackWithAnalytics,
  withAnalyticsEvents,
  WithAnalyticsEventsProps
} from '@atlaskit/analytics-next';
import { N20, N800 } from '@atlaskit/theme/colors';
import React, { MouseEvent, useState } from 'react';
import styled from 'styled-components';
import { getParameterByName, parseProductKey, trackingData } from '../tracking';

import { Chevron } from './chevron';
import { SiteList } from './site-list';

export type ProductDropdownItemProps = {
  readonly name: string;
  readonly icon: JSX.Element;
  readonly sites: string[];
  readonly domRootId?: string;
} & WithAnalyticsEventsProps;

export type ProductDropdownItemState = {
  readonly isOpen: boolean;
};

export function ProductDropdownItem({
  name,
  icon,
  sites,
  domRootId
}: ProductDropdownItemProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = () => setIsOpen(!isOpen);
  const singleSite = sites[0];

  const trackAndFollowUrl = (label: string, domain: string) => {
    const analyticsData = Object.assign(
      { ...trackingData.siteLink },
      {
        magnoliaComponentId: domRootId,
        atlOrigin: getParameterByName('atlOrigin', domain),
        productKey: parseProductKey(label)
      }
    );

    return useCallbackWithAnalytics((event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      window.location.assign(domain);
    }, analyticsData);
  };

  if (sites.length > 1) {
    return (
      <Container onClick={toggleOpen}>
        <Header>
          {icon}
          <ProductNameLabel>{name}</ProductNameLabel>
          <Chevron isOpen={isOpen} />
        </Header>
        {isOpen ? (
          <SiteList sites={sites} product={name} domRootId={domRootId} />
        ) : null}
      </Container>
    );
  } else {
    return (
      <Container>
        <ProductLink
          href={singleSite}
          onClick={trackAndFollowUrl(name, singleSite)}
        >
          <Header>
            {icon}
            <ProductNameLabel>{name}</ProductNameLabel>
          </Header>
        </ProductLink>
      </Container>
    );
  }
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  padding: 4px 12px;

  &:hover {
    background-color: ${N20};
  }
`;

const ProductLink = styled.a`
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ProductNameLabel = styled.span`
  margin-left: 8px;
  flex: 1;
  font-size: 12px;
  line-height: 14px;
  color: ${N800};
`;

export default withAnalyticsEvents()(ProductDropdownItem);
